import { MJCFParser } from "../../core/mjcfParser";
import { clearBlobUrlCache, clearFileContentCache, clearMeshCache } from "../../core/meshLoader";
import { migratePresets } from "../../core/migrations/presets";
import type { MJCFParseResult } from "../../core/mjcfParser";
import { disposeAll } from "../../runtime/assetCache";
import {
  loadSceneFromParsedResult,
  clearFileHashCache,
  clearFallbackBlobUrls,
} from "../../utils/sceneLoader";
import { ensureDefaultCamerasForLoadedPreset } from "../../utils/defaultSceneCameras";
import { syncLibraryGraspsFromSceneRootBodies } from "../../utils/libraryGraspsSync";
import { logger } from "../../utils/logger";
import { addBreadcrumb, captureError } from "../../monitoring/sentry";
import { getSceneObservabilitySummary } from "../../monitoring/sceneObservability";
import { shouldBlockSceneAuthoringMutation } from "../../utils/physicsGuards";
import { requestSceneReplacementConfirmation } from "../../utils/sceneReplacementGuards";
import { buildProjectSaveSnapshotHash } from "../../utils/projectSaveSnapshot";
import { rememberDiscardedSceneForRestore } from "../../utils/discardedSceneRecovery";
import { getProjectSaveActions } from "../projectSaveStore";
import { usePanelStore } from "../panelStore";
import { getSceneActions, getSceneState } from "../sceneStore";
import { useSelectionStore } from "../selectionStore";
import { getHistoryActions } from "../historyStore";
import type {
  FileStoreActions,
  FileStoreGet,
  FileStoreSet,
  FileMap,
  PresetMap,
  KeyframeDetailsMap,
} from "./types";
import type { BodyUUID } from "../../types/scene";
const KEYFRAME_OWNER_SEPARATOR = " :: ";
const SCENE_BUILD_COMPLETE_EVENT = "scene-build-assets-complete";

type CritterSceneBuildWindow = Window & {
  __critterSceneBuildToken?: unknown;
  __critterSceneAssetsLoading?: unknown;
};

const getErrorMessage = (error: unknown): string => {
  return error instanceof Error ? error.message : String(error || "");
};

const getSceneBuildToken = (): number => {
  if (typeof window === "undefined") return 0;
  const token = Number((window as CritterSceneBuildWindow).__critterSceneBuildToken || 0);
  return Number.isFinite(token) ? token : 0;
};

const isSceneBuildLoading = (): boolean => {
  if (typeof window === "undefined") return false;
  return Boolean((window as CritterSceneBuildWindow).__critterSceneAssetsLoading);
};

const waitForSceneBuildCompletion = async (buildTokenBeforeLoad: number): Promise<void> => {
  if (typeof window === "undefined") return;
  if (!isSceneBuildLoading() && getSceneBuildToken() > buildTokenBeforeLoad) return;

  await new Promise<void>((resolve) => {
    let resolved = false;
    const cleanup = () => {
      if (resolved) return;
      resolved = true;
      window.removeEventListener(SCENE_BUILD_COMPLETE_EVENT, onComplete as EventListener);
      clearTimeout(timeoutId);
      resolve();
    };
    const onComplete = (event: Event) => {
      const detail = (event as CustomEvent<{ buildToken?: number }>)?.detail;
      const completedToken = Number(detail?.buildToken || 0);
      if (completedToken > buildTokenBeforeLoad) {
        cleanup();
      }
    };
    const timeoutId = window.setTimeout(cleanup, 20000);
    window.addEventListener(SCENE_BUILD_COMPLETE_EVENT, onComplete as EventListener);

    // Resolve immediately if the build already finished between checks.
    if (!isSceneBuildLoading() && getSceneBuildToken() > buildTokenBeforeLoad) {
      cleanup();
    }
  });
};

const scopeKeyframesForOwner = (
  keyframes: Record<string, number[]>,
  ownerName?: string
): Record<string, number[]> => {
  if (!ownerName || !ownerName.trim()) return keyframes || {};
  const scoped: Record<string, number[]> = {};
  Object.entries(keyframes || {}).forEach(([name, qpos]) => {
    if (name.includes(KEYFRAME_OWNER_SEPARATOR)) {
      scoped[name] = qpos;
      return;
    }
    scoped[`${ownerName}${KEYFRAME_OWNER_SEPARATOR}${name}`] = qpos;
  });
  return scoped;
};

const scopeKeyframeDetailsForOwner = (
  keyframeDetails: KeyframeDetailsMap,
  ownerName?: string
): KeyframeDetailsMap => {
  if (!ownerName || !ownerName.trim()) return keyframeDetails || {};
  const scoped: KeyframeDetailsMap = {};
  Object.entries(keyframeDetails || {}).forEach(([name, detail]) => {
    if (name.includes(KEYFRAME_OWNER_SEPARATOR)) {
      scoped[name] = detail;
      return;
    }
    scoped[`${ownerName}${KEYFRAME_OWNER_SEPARATOR}${name}`] = detail;
  });
  return scoped;
};

export const createPresetActions = ({
  set,
  get,
  applySceneConfigFromFileMap,
}: {
  set: FileStoreSet;
  get: FileStoreGet;
  applySceneConfigFromFileMap: (fileMap: FileMap) => Promise<void>;
}): Pick<FileStoreActions, "setPresets" | "loadPresets" | "loadPresetFolder"> => ({
  setPresets: (presets: PresetMap) => {
    set({
      presets,
      assetFolders: Object.keys(presets),
    });
  },

  loadPresets: async (): Promise<void> => {
    try {
      const response = await fetch("/presets.json");
      const raw = (await response.json()) as unknown;
      const migrated = migratePresets(raw);
      const presets: PresetMap = migrated?.presets ?? {};
      set({
        presets,
        assetFolders: Object.keys(presets),
      });
    } catch (error) {
      logger.error("Failed to load presets:", error);
      set({ presets: {} });
    }
  },

  loadPresetFolder: async (folder: string, options?: { forceReload?: boolean }): Promise<void> => {
    if (shouldBlockSceneAuthoringMutation("loadPresetFolder")) return;
    const { presets } = get();
    const panelActions = usePanelStore.getState().actions;
    const selectionActions = useSelectionStore.getState();
    const forceReload = Boolean(options?.forceReload);
    const buildTokenBeforeLoad = getSceneBuildToken();
    let sceneBuildTriggered = false;

    const currentState = get();
    if (currentState.loadingPreset && currentState.currentLoadingFolder === folder) {
      logger.debug(`[loadPresetFolder] Already loading ${folder}, skipping duplicate request`);
      return;
    }

    if (
      !forceReload &&
      currentState.currentLoadingFolder === folder &&
      currentState.fileMap &&
      Object.keys(currentState.fileMap).length > 0 &&
      (getSceneState().worldBodies || []).length > 0
    ) {
      logger.debug(`[loadPresetFolder] Scene ${folder} already loaded, reusing existing files`);
      panelActions.setLoadingPreset(false);
      return;
    }

    const hasFileStoreWork = Boolean(
      currentState.xmlFile ||
      Object.keys(currentState.fileMap || {}).length > 0 ||
      Object.keys(currentState.keyframes || {}).length > 0 ||
      currentState.currentLoadingFolder
    );
    const replacesCurrentScene =
      forceReload ||
      hasFileStoreWork ||
      (currentState.currentLoadingFolder && currentState.currentLoadingFolder !== folder) ||
      (currentState.currentLoadingFolder !== folder &&
        (getSceneState().worldBodies || []).length > 0);
    if (
      replacesCurrentScene &&
      !(await requestSceneReplacementConfirmation({
        title: "Load Preset?",
        message: "Save before loading this preset?",
        confirmLabel: "Don't Save",
        cancelLabel: "Cancel",
        hasFileStoreWork,
        currentSnapshotHash: buildProjectSaveSnapshotHash({
          scene: getSceneState(),
          fileState: currentState,
        }),
      }))
    )
      return;
    rememberDiscardedSceneForRestore();
    getHistoryActions().clearHistory();

    panelActions.setLoadingPreset(true);
    panelActions.setLoadingProgress(0);
    panelActions.setLoadingMessage(`Loading ${folder}...`);
    panelActions.setLoadingPhase("idle");

    const folderPath = `/robots/${folder}/`;
    const preset = presets[folder];

    if (!preset || !Array.isArray(preset.files) || preset.files.length === 0) {
      logger.error(`[loadPresetFolder] No files found for ${folder}`);
      panelActions.setLoadingPreset(false);
      return;
    }

    addBreadcrumb("preset_load_started", {
      area: "mjcf-import",
      action: "loadPresetFolder",
      operation: "preset-load",
      phase: "started",
      isBuiltinPreset: true,
      presetCatalogId: folder,
      assetCount: preset.files.length,
      resetScene: replacesCurrentScene,
    });

    try {
      try {
        disposeAll();
      } catch {
        // ignore cache disposal errors
      }

      if (currentState.currentLoadingFolder && currentState.currentLoadingFolder !== folder) {
        logger.debug(
          `[loadPresetFolder] Switching from ${currentState.currentLoadingFolder} to ${folder}, clearing scene state`
        );
        getSceneActions().resetScene();
      }

      clearFileContentCache();
      clearBlobUrlCache();
      clearMeshCache();
      clearFileHashCache();
      clearFallbackBlobUrls();

      const xmlFileName = preset.files.find((fileName) => fileName.toLowerCase().endsWith(".xml"));
      let newXmlFile: File | null = null;

      if (xmlFileName) {
        panelActions.setLoadingPhase("xml");
        panelActions.setLoadingMessage("Loading XML file...");
        addBreadcrumb("preset_xml_download_started", {
          area: "mjcf-import",
          action: "loadPresetFolder",
          operation: "preset-load",
          phase: "xml-download",
          isBuiltinPreset: true,
          presetCatalogId: folder,
          fileExtension: "xml",
        });
        try {
          const xmlPath = `${folderPath}${xmlFileName}`;
          const xmlResponse = await fetch(xmlPath, { cache: "no-store" });
          if (xmlResponse.ok) {
            const xmlBlob = await xmlResponse.blob();
            newXmlFile = new File([xmlBlob], xmlFileName, { type: xmlBlob.type });
            set({ xmlFile: newXmlFile });
            panelActions.setLoadingProgress(30);
            panelActions.setLoadingPhase("structure");
            panelActions.setLoadingMessage("Building scene structure...");
          }
        } catch (error) {
          logger.error(`[loadPresetFolder] Error loading XML file ${xmlFileName}:`, error);
        }
      }

      const nonXmlFiles = preset.files.filter(
        (fileName) => !fileName.toLowerCase().endsWith(".xml")
      );

      const loadFile = async (
        fileName: string,
        retryCount = 0
      ): Promise<{ fname: string; file: File } | null> => {
        try {
          const filePath = `${folderPath}${fileName}`;
          const response = await fetch(filePath, { cache: "no-store" });
          if (!response.ok) {
            logger.warn(`[loadPresetFolder] Failed to load ${fileName}: ${response.status}`);
            return null;
          }
          const blob = await response.blob();
          return { fname: fileName, file: new File([blob], fileName, { type: blob.type }) };
        } catch (error) {
          const errorMessage = getErrorMessage(error);
          const isNetworkError =
            errorMessage.includes("ERR_NETWORK_CHANGED") ||
            errorMessage.includes("Failed to fetch") ||
            (error instanceof Error && error.name === "TypeError");

          if (isNetworkError && retryCount < 1) {
            await new Promise((resolve) => setTimeout(resolve, 100));
            return loadFile(fileName, retryCount + 1);
          }

          if (isNetworkError) {
            logger.warn(
              `[loadPresetFolder] Network error loading ${fileName} (may be transient):`,
              errorMessage || error
            );
          } else {
            logger.error(`[loadPresetFolder] Error loading ${fileName}:`, error);
          }
          return null;
        }
      };

      panelActions.setLoadingPhase("meshes");
      panelActions.setLoadingMessage("Starting file downloads...");
      panelActions.setLoadingProgress(40);

      const allPromises: Array<Promise<{ fname: string; file: File } | null>> = nonXmlFiles.map(
        (fileName) => loadFile(fileName)
      );
      const settledResults = await Promise.allSettled(allPromises);
      addBreadcrumb("preset_assets_downloaded", {
        area: "mjcf-import",
        action: "loadPresetFolder",
        operation: "preset-load",
        phase: "assets-downloaded",
        isBuiltinPreset: true,
        presetCatalogId: folder,
        assetCount: nonXmlFiles.length,
      });

      const results: Array<{ fname: string; file: File }> = [];
      let completedCount = 0;
      const totalFiles = nonXmlFiles.length;

      settledResults.forEach((result) => {
        if (result.status === "fulfilled" && result.value) {
          results.push(result.value);
        }
        completedCount += 1;
        const ratio = totalFiles > 0 ? completedCount / totalFiles : 1;
        const progress = 40 + Math.floor(ratio * 50);
        panelActions.setLoadingProgress(progress);
        panelActions.setLoadingMessage(
          `Loading meshes... ${completedCount}/${Math.max(totalFiles, 1)}`
        );
      });

      const newFileMap: FileMap = {};
      results.forEach(({ fname, file }) => {
        newFileMap[fname] = file;
      });

      if (newXmlFile && xmlFileName) {
        newFileMap[xmlFileName] = newXmlFile;
      }

      panelActions.setLoadingMessage("Finalizing...");
      panelActions.setLoadingProgress(95);

      const presetHasGripper =
        preset.gripperFolder &&
        preset.gripperXml &&
        preset.gripperFolder.length > 0 &&
        preset.gripperXml.length > 0;

      if (presetHasGripper && newXmlFile) {
        panelActions.setLoadingMessage("Loading gripper...");
        const gripperPath = `/${preset.gripperFolder}/`;
        const gripperXmlPath = `${gripperPath}${preset.gripperXml}`;
        const gripperXmlResponse = await fetch(gripperXmlPath, { cache: "no-store" });
        if (!gripperXmlResponse.ok) {
          logger.warn(
            `[loadPresetFolder] Failed to load gripper XML ${gripperXmlPath}: ${gripperXmlResponse.status}`
          );
          set({
            fileMap: newFileMap,
            xmlFile: newXmlFile,
            currentLoadingFolder: folder,
          });
        } else {
          const gripperXmlBlob = await gripperXmlResponse.blob();
          const gripperXmlFile = new File([gripperXmlBlob], preset.gripperXml!, {
            type: gripperXmlBlob.type,
          });
          const gripperXmlText = await gripperXmlBlob.text();
          const gripperDoc = new DOMParser().parseFromString(gripperXmlText, "text/xml");
          const compilerEl = gripperDoc.querySelector("compiler");
          const meshdir = compilerEl?.getAttribute("meshdir") || "assets";
          const meshEls = gripperDoc.querySelectorAll("mesh");
          const gripperFileMap: FileMap = {
            [preset.gripperXml!]: gripperXmlFile,
          };
          for (const el of Array.from(meshEls)) {
            const fileAttr = el.getAttribute("file");
            if (!fileAttr) continue;
            let actualPath = fileAttr;
            if (!fileAttr.startsWith("assets/") && !fileAttr.includes("/") && meshdir) {
              actualPath = `${meshdir}/${fileAttr}`;
            }
            if (!actualPath.startsWith("assets/") && !actualPath.startsWith(meshdir + "/")) {
              continue;
            }
            const meshPath = `${gripperPath}${actualPath}`;
            try {
              const meshRes = await fetch(meshPath, { cache: "no-store" });
              if (meshRes.ok) {
                const blob = await meshRes.blob();
                gripperFileMap[actualPath] = new File([blob], actualPath, {
                  type: blob.type,
                });
              }
            } catch (e) {
              logger.warn(`[loadPresetFolder] Failed to load gripper mesh ${actualPath}:`, e);
            }
          }
          const mergedFileMap: FileMap = {
            ...newFileMap,
            ...gripperFileMap,
          };
          set({ parsingXml: true });
          const sceneActions = getSceneActions();
          const parser = new MJCFParser();
          const robotParsed = (await parser.parseFile(newXmlFile)) as MJCFParseResult;
          const gripperParsed = (await parser.parseFile(gripperXmlFile)) as MJCFParseResult;
          const mergedParsed = {
            materials: {
              ...(robotParsed.materials || {}),
              ...(gripperParsed.materials || {}),
            },
            joints: [...(robotParsed.joints || []), ...(gripperParsed.joints || [])],
            bodies: {
              ...(robotParsed.bodies || {}),
              ...(gripperParsed.bodies || {}),
            },
            worldBodies: [...(robotParsed.worldBodies || []), ...(gripperParsed.worldBodies || [])],
            sites: {
              ...(robotParsed.sites || {}),
              ...(gripperParsed.sites || {}),
            },
            textures: {
              ...(robotParsed.textures || {}),
              ...(gripperParsed.textures || {}),
            },
            meshes: {
              ...(robotParsed.meshes || {}),
              ...(gripperParsed.meshes || {}),
            },
            actuators: {
              ...(robotParsed.actuators || {}),
              ...(gripperParsed.actuators || {}),
            },
            tendons: {
              ...(robotParsed.tendons || {}),
              ...(gripperParsed.tendons || {}),
            },
            sensors: {
              ...(robotParsed.sensors || {}),
              ...(gripperParsed.sensors || {}),
            },
            equalities: {
              ...(robotParsed.equalities || {}),
              ...(gripperParsed.equalities || {}),
            },
            // Preserve robot keyframes so loadSceneFromParsedResult can initialize
            // joints from the robot's "home" pose for preset scenes with grippers.
            keyframes: robotParsed.keyframes || {},
            keyframeDetails: robotParsed.keyframeDetails || {},
            preservedMjcfSections: robotParsed.preservedMjcfSections || {},
          } as MJCFParseResult;
          sceneBuildTriggered = true;
          await loadSceneFromParsedResult(mergedParsed, sceneActions, {
            resetScene: true,
            mergeWithExisting: false,
            processAssets: true,
            saveToHistory: true,
            fileMap: mergedFileMap,
            worldBodyNameBase: preset?.name,
          });
          ensureDefaultCamerasForLoadedPreset({
            presetFolder: folder,
            logTag: `load_preset:${folder}`,
          });
          const worldBodies = getSceneState().worldBodies || [];
          if (worldBodies.length >= 2) {
            const robotUuid =
              typeof worldBodies[0] === "string"
                ? worldBodies[0]
                : (worldBodies[0] as { uuid: string }).uuid;
            const gripperUuid =
              typeof worldBodies[1] === "string"
                ? worldBodies[1]
                : (worldBodies[1] as { uuid: string }).uuid;
            sceneActions.attachBySites(gripperUuid as BodyUUID, robotUuid as BodyUUID);
          }
          const referencedFiles = robotParsed.referencedFiles || [];
          const keyframes = scopeKeyframesForOwner(
            robotParsed.keyframes || {},
            preset?.name || folder
          );
          const keyframeDetails = scopeKeyframeDetailsForOwner(
            robotParsed.keyframeDetails || {},
            preset?.name || folder
          );
          const missing = referencedFiles.filter((filename) => {
            if (mergedFileMap[filename]) return false;
            if (mergedFileMap[`assets/${filename}`]) return false;
            if (filename.startsWith("assets/") && mergedFileMap[filename.replace("assets/", "")]) {
              return false;
            }
            return true;
          });
          set({
            fileMap: mergedFileMap,
            xmlFile: null,
            currentLoadingFolder: folder,
            referencedFiles,
            keyframes,
            keyframeDetails,
            preservedMjcfSections: robotParsed.preservedMjcfSections || {},
            missingFiles: missing,
            parsingXml: false,
          });
        }
      } else {
        // Parse directly so loadPresetFolder only resolves after the scene is fully built.
        // Setting xmlFile: null (never xmlFile: newXmlFile) prevents the App-level xml effect
        // from firing a redundant second parse.
        if (newXmlFile) {
          set({ parsingXml: true });
          try {
            const parser = new MJCFParser();
            const parsedResult = (await parser.parseFile(newXmlFile)) as MJCFParseResult;
            sceneBuildTriggered = true;
            await loadSceneFromParsedResult(parsedResult, getSceneActions(), {
              resetScene: true,
              mergeWithExisting: false,
              processAssets: true,
              saveToHistory: true,
              fileMap: newFileMap,
              worldBodyNameBase: preset?.name,
            });
            ensureDefaultCamerasForLoadedPreset({
              presetFolder: folder,
              logTag: `load_preset:${folder}`,
            });
            await applySceneConfigFromFileMap(newFileMap);
            await syncLibraryGraspsFromSceneRootBodies();
            const missing = (parsedResult.referencedFiles || []).filter((filename) => {
              if (newFileMap[filename]) return false;
              if (newFileMap[`assets/${filename}`]) return false;
              if (filename.startsWith("assets/") && newFileMap[filename.replace("assets/", "")]) {
                return false;
              }
              return true;
            });
            set({
              fileMap: newFileMap,
              xmlFile: null,
              currentLoadingFolder: folder,
              referencedFiles: parsedResult.referencedFiles || [],
              keyframes: scopeKeyframesForOwner(
                parsedResult.keyframes || {},
                preset?.name || folder
              ),
              keyframeDetails: scopeKeyframeDetailsForOwner(
                parsedResult.keyframeDetails || {},
                preset?.name || folder
              ),
              preservedMjcfSections: parsedResult.preservedMjcfSections || {},
              missingFiles: missing,
              parsingXml: false,
            });
            addBreadcrumb("preset_scene_loaded", {
              area: "mjcf-import",
              action: "loadPresetFolder.parseXml",
              operation: "preset-load",
              phase: "scene-loaded",
              isBuiltinPreset: true,
              presetCatalogId: folder,
              missingAssetCount: missing.length,
              ...getSceneObservabilitySummary(getSceneState()),
            });
          } catch (error) {
            logger.error(`[loadPresetFolder] Error parsing XML for ${folder}:`, error);
            addBreadcrumb(
              "preset_parse_failed",
              {
                area: "mjcf-import",
                action: "loadPresetFolder.parseXml",
                operation: "preset-load",
                phase: "failed",
                isBuiltinPreset: true,
                presetCatalogId: folder,
              },
              "error"
            );
            captureError(error, {
              area: "mjcf-import",
              action: "loadPresetFolder.parseXml",
              presetFolder: folder,
              fileName: newXmlFile.name || null,
            });
            set({
              fileMap: newFileMap,
              xmlFile: null,
              currentLoadingFolder: folder,
              parsingXml: false,
            });
          }
        } else {
          set({ fileMap: newFileMap, xmlFile: null, currentLoadingFolder: folder });
        }
      }

      if (sceneBuildTriggered) {
        await waitForSceneBuildCompletion(buildTokenBeforeLoad);
      }
      getProjectSaveActions().markProjectSaved(
        buildProjectSaveSnapshotHash({
          scene: getSceneState(),
          fileState: get(),
        }),
        "import"
      );
      getHistoryActions().clearHistory();

      panelActions.setLoadingProgress(100);
      panelActions.setLoadingPhase("complete");
      panelActions.setLoadingMessage("Complete!");
    } catch (error) {
      logger.error(`[loadPresetFolder] Error loading preset folder ${folder}:`, error);
      addBreadcrumb(
        "preset_load_failed",
        {
          area: "mjcf-import",
          action: "loadPresetFolder",
          operation: "preset-load",
          phase: "failed",
          isBuiltinPreset: true,
          presetCatalogId: folder,
        },
        "error"
      );
      captureError(error, {
        area: "mjcf-import",
        action: "loadPresetFolder",
        presetFolder: folder,
      });
      const message =
        error instanceof Error && error.message
          ? `Error loading scene: ${error.message}`
          : `Error loading scene: ${String(error || "Unknown error")}`;
      panelActions.setLoadingMessage(message);
    } finally {
      selectionActions.clearAllSelections();
      selectionActions.clearAllHovers();
      panelActions.setExpandedBodies({});
      panelActions.setLoadingPreset(false);

      setTimeout(() => {
        panelActions.setLoadingProgress(0);
        panelActions.setLoadingMessage("");
        panelActions.setLoadingPhase("idle");
      }, 1000);
    }
  },
});
